import { BaseService } from '../BaseService';
import { 
  BankTransaction, 
  BankAccount, 
  BankReconciliation,
  BankConnection,
  BankTransferRecord,
  TransferType
} from '../../types/banking';
import { ValidationError } from '../../utils/errors';
import { PoolClient, QueryResult, QueryResultRow } from 'pg';

export interface BankingProvider {
  name: string;
  capabilities: {
    realTimeTransactions: boolean;
    batchTransactions: boolean;
    accountBalances: boolean;
    wireTransfers: boolean;
    ach: boolean;
    checks: boolean;
  };
  apiConfig: {
    baseUrl: string;
    version: string;
    timeout: number;
  };
}

export class BankingService extends BaseService {
  private provider: BankingProvider;
  
  constructor(provider?: BankingProvider) {
    super();
    this.provider = provider || {
      name: 'LinkBank',
      capabilities: {
        realTimeTransactions: false,
        batchTransactions: true,
        accountBalances: true,
        wireTransfers: true,
        ach: true,
        checks: true
      },
      apiConfig: {
        baseUrl: process.env.BANK_API_URL || '',
        version: 'v1',
        timeout: 30000
      }
    };
  }

  protected async runQuery<T extends QueryResultRow = any>(
    query: string,
    params: any[],
    client?: PoolClient
  ): Promise<QueryResult<T>> {
    if (client) {
      // If client is provided, we're in a transaction
      return await client.query(query, params);
    } else {
      // Otherwise use our BaseService executeQuery
      return await this.executeQuery(query, params);
    }
  }

  async getAccountBalance(accountId: string): Promise<number> {
    return await this.executeTransaction(async (client) => {
      const query = `
        SELECT current_balance 
        FROM sponsfi.bank_accounts 
        WHERE id = $1
      `;
      
      const result = await this.runQuery<{ current_balance: number }>(
        query,
        [accountId],
        client
      );
      return result.rows[0]?.current_balance || 0;
    });
  }

  protected async executeTransaction<T>(callback: (client: PoolClient) => Promise<T>): Promise<T> {
    if (typeof window !== 'undefined') {
      // In browser environment, use API endpoint
      throw new Error('Transactions are not supported in browser environment yet. Please implement a transaction API endpoint.');
    } else {
      // In Node.js environment, use pg directly
      try {
        const { Pool } = await import('pg');
        const pool = new Pool({
          connectionString: process.env.DATABASE_URL,
          ssl: process.env.NODE_ENV === 'production' ? { rejectUnauthorized: false } : false
        });
        
        const client = await pool.connect();
        try {
          await client.query('BEGIN');
          const result = await callback(client);
          await client.query('COMMIT');
          return result;
        } catch (error) {
          await client.query('ROLLBACK');
          throw error;
        } finally {
          client.release();
        }
      } catch (error) {
        console.error('Transaction error:', error);
        throw error;
      }
    }
  }

  async executeTransfer(transferData: {
    fromAccount: string;
    toAccount: string;
    amount: number;
    type?: TransferType;
    metadata?: Record<string, any>;
  }): Promise<BankTransferRecord> {
    return await this.executeTransaction(async (client) => {
      const sourceQuery = `
        SELECT current_balance 
        FROM sponsfi.bank_accounts 
        WHERE id = $1
      `;
      
      const sourceResult = await this.runQuery(
        sourceQuery,
        [transferData.fromAccount],
        client
      );
      
      const sourceBalance = sourceResult.rows[0]?.current_balance || 0;
      
      if (sourceBalance < transferData.amount) {
        throw new ValidationError('Insufficient funds for transfer');
      }
      
      const updateSourceQuery = `
        UPDATE sponsfi.bank_accounts
        SET current_balance = current_balance - $2
        WHERE id = $1
      `;
      
      await this.runQuery(
        updateSourceQuery,
        [transferData.fromAccount, transferData.amount],
        client
      );
      
      const updateDestQuery = `
        UPDATE sponsfi.bank_accounts
        SET current_balance = current_balance + $2
        WHERE id = $1
      `;
      
      await this.runQuery(
        updateDestQuery,
        [transferData.toAccount, transferData.amount],
        client
      );
      
      // Create transfer record
      const recordQuery = `
        INSERT INTO sponsfi.bank_transfers (
          from_account,
          to_account,
          amount,
          type,
          status,
          created_at,
          metadata
        ) VALUES ($1, $2, $3, $4, $5, $6, $7)
        RETURNING *
      `;
      
      const recordResult = await this.runQuery(
        recordQuery,
        [
          transferData.fromAccount,
          transferData.toAccount,
          transferData.amount,
          transferData.type || 'ach',
          'completed',
          new Date(),
          JSON.stringify(transferData.metadata || {})
        ],
        client
      );
      
      return recordResult.rows[0] as BankTransferRecord;
    });
  }

  private async getInternalTransactions(
    client: PoolClient,
    accountId: string,
    startDate: Date,
    endDate: Date
  ): Promise<BankTransaction[]> {
    const query = `
      SELECT * FROM sponsfi.transactions 
      WHERE account_id = $1 
      AND created_at BETWEEN $2 AND $3
    `;
    const result = await this.runQuery<BankTransaction>(query, [accountId, startDate, endDate], client);
    return result.rows;
  }

  private async getBankTransactions(
    client: PoolClient,
    accountId: string,
    startDate: Date,
    endDate: Date
  ): Promise<BankTransaction[]> {
    // Implementation for fetching transactions from bank API
    return [];
  }

  private async performReconciliation(
    client: PoolClient,
    internalTxns: BankTransaction[],
    bankTxns: BankTransaction[]
  ): Promise<BankReconciliation> {
    const now = new Date();
    
    // Implementation for reconciliation logic
    return {
      id: `recon_${Date.now()}`,
      accountId: internalTxns[0]?.accountId || '',
      startDate: internalTxns[0]?.date || now,
      endDate: now,
      startingBalance: 0, // This should be calculated
      endingBalance: 0,   // This should be calculated
      matchedTransactions: [], // Empty array of BankTransaction instead of [0]
      unmatchedInternal: internalTxns,
      unmatchedBank: bankTxns,
      reconciliationDate: now,
      status: 'completed'
    };
  }

  async reconcileTransactions(
    startDate: Date,
    endDate: Date,
    accountId: string
  ): Promise<BankReconciliation> {
    return await this.executeTransaction(async (client) => {
      const internalTxns = await this.getInternalTransactions(
        client,
        accountId,
        startDate,
        endDate
      );

      const bankTxns = await this.getBankTransactions(
        client,
        accountId,
        startDate,
        endDate
      );

      return await this.performReconciliation(
        client,
        internalTxns,
        bankTxns
      );
    });
  }

  private async createTransferRecord(
    client: PoolClient,
    transfer: {
      fromAccount: string;
      toAccount: string;
      amount: number;
      type: TransferType;
      metadata?: Record<string, any>;
    }
  ): Promise<BankTransferRecord> {
    // Implementation for creating transfer record
    return {
      id: 'transfer_' + Date.now(),
      fromAccount: transfer.fromAccount,
      toAccount: transfer.toAccount,
      amount: transfer.amount,
      type: transfer.type,
      status: 'pending',
      createdAt: new Date(),
      metadata: transfer.metadata || {}
    };
  }

  private async createPendingTransactions(
    client: PoolClient,
    transfer: BankTransferRecord
  ): Promise<void> {
    // Implementation for creating pending transactions
  }

  async initiateTransfer(transfer: {
    fromAccount: string;
    toAccount: string;
    amount: number;
    type: TransferType;
    metadata?: Record<string, any>;
  }): Promise<BankTransferRecord> {
    return await this.executeTransaction(async (client) => {
      const transferRecord = await this.createTransferRecord(
        client,
        transfer
      );

      await this.createPendingTransactions(client, transferRecord);

      return {
        ...transferRecord,
        estimatedCompletion: this.calculateEstimatedCompletion(transfer.type)
      };
    });
  }

  private calculateEstimatedCompletion(transferType: TransferType): Date {
    const now = new Date();
    switch (transferType) {
      case 'ACH':
        return new Date(now.setDate(now.getDate() + 3));
      case 'WIRE':
        return new Date(now.setHours(now.getHours() + 2));
      case 'INTERNAL':
        return now;
      default:
        return new Date(now.setDate(now.getDate() + 1));
    }
  }

  async getBankConnections(fundId: string): Promise<BankConnection[]> {
    return await this.executeTransaction(async (client) => {
      const query = `
        SELECT * FROM sponsfi.bank_connections
        WHERE fund_id = $1
      `;
      const result = await this.runQuery<BankConnection>(query, [fundId], client);
      return result.rows;
    });
  }

  async getTransactions(
    bankId: string, 
    dateRange: { start: Date; end: Date }
  ): Promise<BankTransaction[]> {
    return await this.executeTransaction(async (client) => {
      const query = `
        SELECT * FROM sponsfi.bank_transactions
        WHERE bank_id = $1
        AND transaction_date BETWEEN $2 AND $3
      `;
      const result = await this.runQuery<BankTransaction>(
        query, 
        [bankId, dateRange.start, dateRange.end],
        client
      );
      return result.rows;
    });
  }
} 